<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      v-model="selectedAnalysisRequests"
      :pagination="pagination"
      :iconHeader="hasPermission('analysisRequests.add') && hasPermission('nodes.tree.view') ? 'add' : null"
      :loading="loading"
      :isInfinitScroll="true"
      :headerActionButtons="getHeaderActionButtons"
      :items="displayedAnalysisRequests"
      :isSearchable="false"
      :showSelect="!(isRep || isSalesManager) && !isSite"
      :showExpand="!(isRep || isSalesManager) && !isSite"
      :headers="analysisRequestHeaders"
      :title="$t('analysisRequests.pageName')"
      :centerColumns="['billable', 'labWorkOrder', 'workOrder']"
      :notSortableColumns="['billable', 'labWorkOrder', 'workOrder']"
      :sortableColumns="sortableColumns"
      hideDefaultFooter
      @iconAction="(action)=>showAddAnalysisRequestModal()"
      @sendQueryParams="fetchQuery"
    >
      <template #extraHeader>
        <div class="download-container">
          <v-btn
            color="secondary btn-download"
            class="m-2"
          >
            <v-icon>
              save_alt
            </v-icon>
            {{ $t('laboratory.labels.downloadXML') }}
          </v-btn>
        </div>
      </template>
      <template #SearchWithFilters>
        <SearchWithFilters
          :filters="filters"
          :searchPlaceholder="$t('laboratory.placeholder.searchBy')"
          :datesRange="true"
          :filterName="filterName"
          :saveFilter="true"
          @onApplyFilters="handleApplyFilters"
          @onSearch="handleSearch"
        />
      </template>
      <template #item.analysisAction="{item}">
        <div :class="{'disable-action': notShowItemAction(item)}">
          <VTableAction
            class="action-buttons"
            :itemActions="getActions(item)"
            :isCustomIcon="true"
            :verticalActionDots="true"
            @iconAction="(action)=>actionButton(item, action)"
          />
        </div>
      </template>
      <template
        v-if="!isSite"
        #buttonsLineHeader
      >
        <v-row class="m-2 analysisRequest-action-bar">
          <v-btn
            small
            color="secondary"
            class="siteTree-btn m-2"
            data-cy="createWorkOrder"
            :disabled="cannotAddWorkOrder"
            @click.stop="debouncedAddWorkOrder"
          >
            {{ $t('laboratory.labels.createWorkOrder') }}
          </v-btn>
          <v-btn
            small
            color="secondary"
            class="siteTree-btn m-2"
            data-cy="addToExistingWO"
            :disabled="cannotAddToExisitingWorkOrder"
            @click.stop="onClickAddToExistingWorkOrder"
          >
            {{ $t('laboratory.labels.addToExistingWorkOrder') }}
          </v-btn>
          <v-btn
            v-if="hasPermission('analysisRequests.remove')"
            small
            color="secondary"
            class="siteTree-btn m-2"
            data-cy="removeWOrkOrder"
            :disabled="cannotRemoveWorkOrder"
            @click.stop="onClickConfirmRemove"
          >
            {{ $t('forms.btn.delete') }}
          </v-btn>
        </v-row>
      </template>
      <template #item.status="{ item }">
        {{ $t(`laboratory.labels.${item.status.toLowerCase()}`) }}
      </template>
      <template #item.workOrder="{ item }">
        <v-btn
          tile
          icon
          size="24"
          color="#26384b"
          :disabled="!item.workOrderId"
          @click="redirectToWO(item)"
        >
          <v-icon>mdi-link-variant</v-icon>
        </v-btn>
      </template>
      <template #item.labWorkOrder="{ item }">
        <div class="linkWO">
          <v-btn
            tile
            icon
            size="24"
            color="#26384b"
            :disabled="!item.labWorkOrderId"
            @click="onClickRedirect(item.labWorkOrderId, item.client.id, item.site.id)"
          >
            <v-icon>mdi-link-variant</v-icon>
          </v-btn>
        </div>
      </template>
      <template #item.billable="{item}">
        <v-checkbox
          v-model="item.billable"
          dense
          readonly
          disabled
          color="primary"
          class="mt-0"
          hideDetails
        />
      </template>
      <template #item.requestedBy="{item}">
        <v-btn
          text
          small
          class="pl-0"
          @click.stop="onCollectedByChange(item)"
        >
          <span class="mr-1">{{ item.requestedBy }}</span>
          <v-icon small>
            mdi-pencil-outline
          </v-icon>
        </v-btn>
      </template>
      <template #expanded-item="props">
        <td :colspan="props.headers.length">
          <VTableLocal
            :items="props.item.samples"
            disablePagination
            :headers="sampleHeaders"
            class="blocSample"
          >
            <template #item.labId="{ item }">
              <v-edit-dialog
                @close="onUpdateSample(item, 'labId')"
              >
                {{ item.labId }}
                <template #input>
                  <VTextField
                    v-model="item.labId"
                    :label="$t('analysisRequests.labels.labId')"
                    data-cy="labId"
                  />
                </template>
              </v-edit-dialog>
            </template>
            <template #item.collectedDate="{item}">
              <VTextField
                :value="$options.filters.formatDate(item.collectedDate, 'L LT')"
                :isOutlined="false"
                data-cy="labId"
                readonly
              />
            </template>
            <template #item.receivedDate="{item}">
              <VDateTimePicker
                v-model="item.receivedDate"
                :isOutlined="false"
                :disabled="sampleOfArInTreatment(item)"
                :locale="datePickerLocales[$store.getters.lang]"
                class="receivedDate"
                @input="onUpdateSample(item, 'receivedDate')"
              />
            </template>
            <template
              v-if="checkIsItClickable(props)"
              #item.status="{ item }"
            >
              <v-edit-dialog
                :ref="`editDialogRef_${item.id}`"
                @close="onUpdateSample(item, 'status')"
              >
                {{ $t(`laboratory.labels.${item.status.toLowerCase()}`) }}
                <template #input>
                  <VSelect
                    v-model="item.status"
                    :label="$t('analysisRequests.labels.status')"
                    :items="sampleStatus"
                    data-cy="sampleStatus"
                    @change="onUpdateSample(item, 'status')"
                  />
                </template>
              </v-edit-dialog>
            </template>
            <template
              v-else
              #item.status="{ item }"
            >
              {{ $t(`laboratory.labels.${item.status.toLowerCase()}`) }}
            </template>
          </VTableLocal>
        </td>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
    <WizardModal ref="wizardModal" />
  </div>
</template>

<script>
import { cloneDeep, debounce } from 'lodash';
import moment from 'moment';
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import roles from '@/constants/roles';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import SampleForm from '@/components/forms/sample/SampleForm';
import VTableLocal from '@/components/shared/content-views/VTableLocal';
import WizardModal from '@/components/shared/forms-controls/WizardModal';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect';
import sampleStatus from '@/constants/sampleStatus';
import analysisRequestStatus from '@/constants/analysisRequestStatus';
import sampleTypes from '@/constants/sampleTypes';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import VTableAction from '@/components/shared/widgets/VTableAction';
import AnanlysisAddToExistingWorkOrderForm from '@/components/AnalysisAddToExistingWorkOrderForm';
import VDateTimePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDateTimePicker';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import _datePickerLocales from '@/locales/date-picker-locales';
import AddAutomaticSentenceForm from '@/components/sites/visitSummaryComponents/AddAutomaticSentenceForm';
import filterMixin from '@/mixins/filterMixin';
import perpage from '@/constants/pagination';
import { SAMPLE_TYPE_LEGIONELLA_CULTURE } from '@/aquassist-shared/constants/types/samples';
import CollectedByForm from './CollectedByForm.vue';

export default {
    components: {
        VSelect,
        VTableLocal,
        TableRemote,
        WizardModal,
        VTableAction,
        VDateTimePicker,
        SearchWithFilters,
    },
    mixins: [
        filterMixin,
    ],
    props: {
        siteId: {
            type: String,
            default: null,
        },
        isTemplate: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            displayedRequests: [],
            isInitializing: false,
            selectedAnalysisRequests: [],
            debouncedAddWorkOrder: debounce(this.onClickAddWorkOrder, 500),
            processing: false,
            loading: false,
            analysisRequests: [],
            pagination: {},
            filterName: 'analysisRequests',
            queryParams: {
                perpage,
                status: [analysisRequestStatus.RECEIVED.toUpperCase()],
                startDate: moment().subtract(30, 'days').toISOString(),
                endDate: moment().endOf('day').toISOString(),
            },
            filters: [
                {
                    key: 'type',
                    label: 'laboratory.labels.type',
                    type: 'select',
                    value: [],
                    multiple: true,
                    items: Object.values(sampleTypes).map(sType => ({
                        text: `sampleTypes.${sType.charAt(0).toLowerCase()}${sType.substring(1)}`,
                        value: sType,
                    })),
                    datacy: 'analysisRequestType',
                },
                {
                    key: 'status',
                    label: 'laboratory.labels.status',
                    type: 'select',
                    value: [analysisRequestStatus.RECEIVED.toUpperCase()],
                    multiple: true,
                    items: Object.keys(analysisRequestStatus).map(sStatus => ({
                        text: `laboratory.labels.${sStatus.toLowerCase()}`,
                        value: sStatus,
                    })),
                    datacy: 'analysisRequestStatus',
                },
                {
                    key: 'date',
                    label: 'laboratory.labels.date',
                    type: 'date',
                    value: [moment().subtract(30, 'days').toISOString(), moment().toISOString()],
                    dateKeys: ['startDate', 'endDate'],
                    isRange: true,
                    datacy: 'analysisRequestDate',
                },
                {
                    key: 'billable',
                    label: 'analysisRequests.labels.billing',
                    type: 'select',
                    value: null,
                    multiple: false,
                    items: [
                        { text: 'analysisRequests.labels.billable', value: true },
                        { text: 'analysisRequests.labels.notBillable', value: false },
                    ],
                    datacy: 'analysisRequestBillable',
                },
            ],
        };
    },
    computed: {
        ...mapGetters([
            'currentMember',
            'hasPermission',
            'hasRole',
            'userLang',
            'client',
        ]),
        isSite() {
            return !!this.siteId?.length;
        },
        getHeaderActionButtons() {
            const actions = [{
                label: this.$t('sitePage.add'),
                action: () => this.showAddAnalysisRequestModal(),
                icon: 'add',
                displayIcon: true,
                color: 'primary',
                desktopExtraClass: 'iconAction',
            }];

            if (this.isSite) {
                actions.push({
                    label: this.$t('sitePage.template'),
                    action: () => this.$emit('onTemplate'),
                    icon: 'view_stream',
                    displayIcon: true,
                    color: 'primary',
                    desktopExtraClass: 'iconAction',
                });
            }
            return actions;
        },
        sortableColumns() {
            return [
                'request',
                'type',
                'sitename',
                'deliveryaccountnumber',
                'component',
                'date',
                'workorder',
                'ponumber',
                'labworkorder',
                'requestedby',
                'status'];
        },
        sampleStatus() {
            return Object.values(sampleStatus).map(status => ({
                name: this.$t(`laboratory.labels.${status}`),
                id: status.toUpperCase(),
            }));
        },
        analysisRequestStatus() {
            return Object.values(analysisRequestStatus).map(status => ({
                name: this.$t(`laboratory.labels.${status}`),
                id: status.toUpperCase(),
            }));
        },
        analysisRequestHeaders() {
            const headerArr = [
                'request',
                'type',
                'siteName',
                'deliveryAccountNumber',
                'component',
                'date',
                'requestedBy',
                'workOrder',
                'billable',
                'poNumber',
                'labWorkOrder',
                'status',
            ].map(value => ({
                text: this.$t(`analysisRequests.labels.${value}`),
                value,
            }));
            headerArr.push({
                sortable: false,
                text: this.$t('controls.actions'),
                value: 'analysisAction',
            });
            return headerArr;
        },
        sampleHeaders() {
            return [
                'sample',
                'collectedDate',
                'receivedDate',
                'labId',
                'status',
            ].map(value => ({
                text: this.$t(`analysisRequests.labels.${value}`),
                value,
            }));
        },
        cannotAddWorkOrder() {
            return this.areAnalysisRequestsUnselected
              || this.areSamplesPending
              || this.areAnalysisRequestsOnDifferentSites
              || this.areAnalysisRequestsInTreatment
              || this.areAllSamplesRefused
              || this.areMultiSelectTypeWithLegioByCulture
              || this.processing;
        },
        cannotAddToExisitingWorkOrder() {
            let exists = true;
            if (!this.cannotAddWorkOrder) {
                if ([roles.ADMINISTRATOR.value,
                    roles.MANAGER.value,
                    roles.LABORATORY_MANAGER.value,
                    roles.LABORATORY_MICROBIOLOGIST.value,
                    roles.LABORATORY_TECHNICIAN.value,
                    roles.LABORATORY_CHEMIST.value].find(role => this.hasRole(role))) {
                    exists = false;
                }
            }
            return exists;
        },
        cannotRemoveWorkOrder() {
            return this.areAnalysisRequestsUnselected
              || this.areAnalysisRequestsInTreatment;
        },
        areAllWOBillableOrNot() {
            const { poNumber } = this.selectedAnalysisRequests[0];
            return !((this.selectedAnalysisRequests
                .every(analysisRequest => analysisRequest.billable) || this.selectedAnalysisRequests
                .every(analysisRequest => !analysisRequest.billable)) && this.selectedAnalysisRequests
                .every(analysisRequest => analysisRequest.poNumber === poNumber));
        },
        areSamplesPending() {
            return this.selectedSamples
                .some(sample => sample.status === sampleStatus.PENDING.toUpperCase());
        },
        areAllSamplesRefused() {
            return this.selectedSamples
                .every(sample => sample.status === sampleStatus.REFUSED.toUpperCase());
        },
        selectedSamples() {
            return this.selectedAnalysisRequests
                .map(analysisRequest => analysisRequest.samples)
                .flat();
        },
        areMultiSelectTypeWithLegioByCulture() {
            const allSelect = this.selectedAnalysisRequests.map(analysisRequest => analysisRequest.samples[0].type);
            if (allSelect.length > 1) {
                if (allSelect.includes(SAMPLE_TYPE_LEGIONELLA_CULTURE)
                && allSelect.some(analysisRequest => analysisRequest !== SAMPLE_TYPE_LEGIONELLA_CULTURE)) {
                    return true;
                }
                if (!allSelect.includes(SAMPLE_TYPE_LEGIONELLA_CULTURE)) {
                    return false;
                }
            }
            return false;
        },
        areAnalysisRequestsOnDifferentSites() {
            return this.selectedAnalysisRequests
                .some(analysisRequest => analysisRequest.site.id !== this.selectedAnalysisRequests[0].site.id);
        },
        areAnalysisRequestsUnselected() {
            return this.selectedAnalysisRequests.length === 0;
        },
        areAnalysisRequestsInTreatment() {
            return this.selectedAnalysisRequests
                .some(analysisRequest => analysisRequest.status === analysisRequestStatus.INTREATMENT.toUpperCase());
        },
        displayedAnalysisRequests() {
            return this.displayedRequests;
        },
        isRep() {
            return this.hasRole(roles.SALES_REPRESENTATIVE);
        },
        isSalesManager() {
            return this.hasRole(roles.SALES_MANAGER);
        },
        datePickerLocales() {
            return _datePickerLocales;
        },
    },
    async created() {
        this.isInitializing = true;
        this.getFilterValues();
        await this.getAnalysisRequests();
        this.isInitializing = false;
    },
    methods: {
        ...mapActions({
            getWorkOrder: 'getWorkOrderInfo',
        }),
        notShowItemAction(itemRef) {
            return ((['INTREATMENT', 'COMPLETED'].includes(itemRef.status)) && ![sampleTypes.LEGIONELLA_CULTRURE, sampleTypes.LEGIONELLOSIS].includes(itemRef.typeOrigin))
                || this.selectedAnalysisRequests.length > 1;
        },
        sampleOfArInTreatment(sample) {
            const analysisRequest = this.analysisRequests.find(ar => ar.samples.some(s => s.id === sample.id));
            return !!analysisRequest?.labWorkOrder;
        },
        formatType(type) {
            if (!type) {
                return '';
            }

            const formattedType = `${type.substring(0, 1).toLowerCase()}${type.substring(1)}`;
            return this.$t(`sampleTypes.${formattedType}`);
        },
        async onUpdateSample(modifiedSample, attribute) {
            if (modifiedSample.status === sampleStatus.REFUSED.toUpperCase()) {
                if (!modifiedSample.reason || !modifiedSample.reason.length) {
                    // eslint-disable-next-line no-param-reassign
                    modifiedSample.reason = '';
                }
                this.$root.$emit('showModal', {
                    title: this.$t('analysisRequests.rejectSample'),
                    component: Vue.component('addAutomaticSentenceForm', AddAutomaticSentenceForm),
                    payload: { lang: this.userLang, isAnalysis: true },
                    onConfirm: async requestPayload => {
                        let comment = requestPayload;
                        if (typeof requestPayload !== 'string') {
                            comment = requestPayload.note;
                        }

                        // eslint-disable-next-line no-param-reassign
                        modifiedSample.reason = `<p>${comment}</p>`;
                        await this.processSampleUpdate(modifiedSample, attribute);
                        const analysisRequest = this.analysisRequests.find(x => x.samples.findIndex(s => s.id === modifiedSample.id) !== -1);
                        const sample = analysisRequest.samples.find(x => x.id === modifiedSample.id);
                        await this.$rq.sendSampleEmail(analysisRequest.id, sample.id);
                    },
                });
            } else {
                this.processSampleUpdate(modifiedSample, attribute);
            }
            const refName = `editDialogRef_${modifiedSample.id}`;
            this.$refs[refName].save();
        },
        async processSampleUpdate(modifiedSample, attribute) {
            const analysisRequest = cloneDeep(this.analysisRequests.find(x => x.samples.findIndex(s => s.id === modifiedSample.id) !== -1));
            const sample = analysisRequest.samples.find(x => x.id === modifiedSample.id);

            if (analysisRequest.type === sampleTypes.COUPON) {
                analysisRequest.samples.forEach(s => {
                    const { pictureBeforeCleaning, pictureAfterCleaning } = s;
                    // eslint-disable-next-line no-param-reassign
                    if (pictureBeforeCleaning) s.pictureBeforeCleaning = pictureBeforeCleaning.id;
                    // eslint-disable-next-line no-param-reassign
                    if (pictureAfterCleaning) s.pictureAfterCleaning = pictureAfterCleaning.id;
                });
            }

            if (sample[attribute] !== modifiedSample[attribute]) {
                if (attribute === 'status') {
                    if (modifiedSample.status !== sampleStatus.PENDING.toUpperCase() && !sample.receivedDate) {
                        sample.receivedDate = moment();
                    }
                    if (modifiedSample.status === sampleStatus.REFUSED.toUpperCase()) {
                        sample.reason = modifiedSample.reason;
                    }
                }
                sample[attribute] = modifiedSample[attribute];
                if (attribute === 'receivedDate') {
                    sample.receivedDate = moment(sample.receivedDate);
                }
                try {
                    await this.updateSample(analysisRequest);
                    // eslint-disable-next-line no-empty
                } catch (error) {}
                await this.fetchQuery({});
            }
        },
        async updateSample(analysisRequest) {
            const payload = cloneDeep(analysisRequest);
            payload.analysisTypes = payload.analysisTypes.map(type => type.key);
            payload.client = payload.client.id;
            payload.site = payload.site.id;
            payload.component = payload.component.id;
            payload.requestedBy = payload.requestedBy.id;

            payload.samples = payload.samples.map(sample => ({
                ...sample,
                collectedBy: payload.requestedBy.id,
            }));

            if (payload.applicableNorms) {
                payload.applicableNorms = payload.applicableNorms.id;
            }

            if (payload.type === sampleTypes.COUPON) {
                payload.samples = payload.samples.map(sample => ({
                    ...sample,
                    coupon: sample.coupon.id,
                }));
            }

            if (payload.labWorkOrder) {
                payload.labWorkOrder = payload.labWorkOrder.id;
            }
            if (payload.workOrder) {
                payload.workOrder = payload.workOrder.id;
            }

            if (payload.selectedAnalysisGroups) {
                payload.selectedAnalysisGroups = payload.selectedAnalysisGroups.map(group => group.id);
            }


            let isDateOk = true;
            payload.samples.forEach((sample, index) => {
                const collectedDateISO = new Date(moment(payload.samples[index].collectedDate).format());
                const receivedDateISO = new Date(moment(payload.samples[index].receivedDate, moment.ISO_8601).format());
                if (collectedDateISO.getTime() > receivedDateISO.getTime()) isDateOk = false;
            });

            if (isDateOk) {
                const { data } = await this.$rq.updateAnalysisRequest(analysisRequest.id, payload);
                this.$toasted.global.toast_success({
                    message: this.$t('analysisRequests.updated', { name: data.requestNumber }),
                });
                await this.fetchQuery({});
            } else {
                this.$toasted.global.toast_error({
                    message: this.$t('analysisRequests.error.dateNotValid'),
                });
            }
        },
        async fetchQuery(queryParams) {
            if (!this.loading) {
                this.queryParams = {
                    ...this.queryParams,
                    ...queryParams,
                    order: this.preparOrderQueryParams(queryParams.order),
                    search: queryParams?.search || '',
                    page: 1,
                };
                this.pagination = {};
                this.analysisRequests = [];
                await this.getAnalysisRequests();
            }
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }
            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'request':
                    newValue = 'requestNumber';
                    break;
                case 'type':
                    newValue = 'type';
                    break;
                case 'sitename':
                    newValue = 'site.name';
                    break;
                case 'deliveryaccountnumber':
                    newValue = 'site.deliveryAccountNumber';
                    break;
                case 'component':
                    newValue = 'component.name';
                    break;
                case 'date':
                    newValue = 'createdAt';
                    break;
                case 'requestedby':
                    newValue = 'requestedBy.user.profile.firstName';
                    break;
                case 'workorder':
                    newValue = 'workOrder';
                    break;
                case 'labworkorder':
                    newValue = 'labWorkOrder.woNumber';
                    break;
                case 'status':
                    newValue = 'status';
                    break;
                case 'ponumber':
                    newValue = 'poNumber';
                    break;
                default:
                    return null;
            }

            return `${newValue}_${order}`;
        },
        prepareSamples(samples) {
            return samples.map(sample => ({
                ...sample,
                sample: sample.identification,
                status: sample.status || sampleStatus.PENDING,
            }));
        },
        async getAnalysisRequests() {
            this.loading = true;
            const params = this.siteId ? { ...this.queryParams, site: this.siteId } : this.queryParams;
            const { data } = await this.$rq.getAnalysisRequests(params);
            this.analysisRequests = [...this.analysisRequests, ...data.analysisRequests];
            await this.loadDisplayedAnalysisRequests();
            this.selectedAnalysisRequests = [];
            this.pagination = data.pagination;
            this.loading = false;
        },
        showAddAnalysisRequestModal() {
            this.$root.$emit('showModal', {
                showNextPrevious: true,
                title: this.$t('analysisRequests.add'),
                component: Vue.component('SampleForm', SampleForm),
                payload: {
                    isEdition: false,
                    isTemplate: this.isTemplate,
                    sample: {
                        site: this.isSite ? this.siteId : null,
                        client: this.isSite ? this.client.id : null,
                    },
                    isLab: true,
                    isSite: this.isSite,
                },
                controlledValidation: true,
                onConfirm: async requestPayload => {
                    await this.addAnalysisRequest(requestPayload);
                },
            });
        },
        prepareAnalysisRequest(analysisRequest) {
            const {
                component,
                site,
                client,
            } = analysisRequest;

            return {
                ...analysisRequest,
                component: component.id,
                site: site.id,
                client: client.id,
            };
        },
        async onClickAddWorkOrder() {
            this.processing = true;
            try {
                const payload = {
                    client: this.selectedAnalysisRequests[0].client.id,
                    site: this.selectedAnalysisRequests[0].site.id,
                    analysisRequests: this.selectedAnalysisRequests.map(analysisRequest => analysisRequest.id),
                };
                const { data } = await this.$rq.addLabWorkOrder(payload);
                this.selectedAnalysisRequests = [];
                this.$toasted.global.toast_success({
                    message: this.$t('labWorkOrders.added', { name: data.woNumber }),
                });
                await this.fetchQuery({});
                this.processing = false;
            } catch (_) {
                this.processing = false;
            }
        },
        async addToExistingWorkOrder(labWorkOrderId) {
            if (labWorkOrderId) {
                const payload = {
                    analysisRequest: this.selectedAnalysisRequests.map(analysisRequest => analysisRequest.id),
                };
                const { data } = await this.$rq.addLabToExistingWorkOrder(payload, labWorkOrderId);
                this.selectedAnalysisRequests = [];
                this.$toasted.global.toast_success({
                    message: this.$t('labWorkOrders.added', { name: data.woNumber }),
                });
                await this.fetchQuery({});
            }
        },
        onClickAddToExistingWorkOrder() {
            this.$root.$emit('showModal', {
                title: this.$t('laboratory.labels.selectWorkOrders'),
                component: Vue.component('AnanlysisAddToExistingWorkOrderForm', AnanlysisAddToExistingWorkOrderForm),
                payload: {
                    siteId: this.selectedAnalysisRequests[0].site.id,
                    type: this.selectedAnalysisRequests[0].typeOrigin,
                    isEdition: true,
                },
                isForm: false,
                onConfirm: requestPayload => this.addToExistingWorkOrder(requestPayload),
            });
        },

        async onClickUpdateAnalysisRequest(analysisRequest, action) {
            if (analysisRequest === 'add') {
                this.showAddAnalysisRequestModal();
            } else if (action === 'edit') {
                await this.showEditAnalysisRequestModal(analysisRequest);
            }
        },

        async showEditAnalysisRequestModal(analysisRequest) {
            if (analysisRequest.status === analysisRequestStatus.INTREATMENT.toUpperCase()
                  || analysisRequest.status === analysisRequestStatus.COMPLETED.toUpperCase()) {
                return;
            }

            const { data } = await this.$rq.getAnalysisRequest(analysisRequest.id);
            this.$root.$emit('showModal', {
                title: this.$t('analysisRequests.update'),
                component: Vue.component('SampleForm', SampleForm),
                payload: {
                    isEdition: true,
                    isLab: true,
                    sample: this.prepareAnalysisRequest(data),
                },
                showNextPrevious: true,
                onConfirm: requestPayload => {
                    const payload = {
                        ...requestPayload,
                        selectedAnalysisGroups: requestPayload.selectedAnalysisGroups?.map(group => {
                            if (typeof group === 'string') {
                                return group;
                            }
                            return group?.id;
                        }),
                    };
                    this.updateAnalysisRequest(analysisRequest.id, payload);
                },
            });
        },

        onClickConfirmRemove() {
            this.$root.$emit('showModal', {
                title: this.$t('analysisRequests.remove'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('analysisRequests.confirmRemove') },
                onConfirm: () => {
                    this.removeAnalysisRequests();
                },
                isForm: false,
            });
        },

        preparePayload(payload) {
            const requestPayload = cloneDeep(payload);
            Object.keys(requestPayload).forEach(key => {
                if (requestPayload[key] === undefined) {
                    delete requestPayload[key];
                } else if (requestPayload[key] && requestPayload[key].id) {
                    requestPayload[key] = requestPayload[key].id;
                }
            });

            if (requestPayload?.samples) {
                requestPayload.samples.forEach(sample => {
                    if (sample.collectedDate) {
                        Vue.set(sample, 'collectedDate', moment(sample.collectedDate).format());
                    }
                    if (sample.installedDate) {
                        Vue.set(sample, 'installedDate', moment(sample.installedDate).format());
                    }
                    if (sample.collectedBy) {
                        Vue.set(sample, 'collectedBy', sample.collectedBy.id);
                    }
                    if (sample.coupon) {
                        Vue.set(sample, 'coupon', sample.coupon.id);
                    }
                });
            }
            requestPayload.billable = payload.billable;
            requestPayload.poNumber = payload.poNumber;
            return requestPayload;
        },

        async addAnalysisRequest(payload) {
            this.loading = true;
            /* Delete spaces before and after the PO Name */
            const copyPayload = cloneDeep(payload);
            if (copyPayload.poNumber) copyPayload.poNumber = copyPayload.poNumber.trim();


            const requestPayload = this.preparePayload(copyPayload);
            requestPayload.requestedBy = this.currentMember.id;
            const { data } = await this.$rq.addAnalysisRequest(requestPayload);
            if (data.isTechFileEmpty) {
                this.$root.$emit('showModal', {
                    title: this.$t('generic.treMissing'),
                    component: Vue.component('ErrorPopup', ConfirmForm),
                    showConfirmButton: false,
                    payload: { msg: data.techFileErrorMsg || '' },
                });
            } else {
                this.$toasted.global.toast_success({
                    message: this.$t('analysisRequests.added', { name: data.requestNumber }),
                });
            }
            await this.handleApplyFilters();
            this.resetStore();
            this.loading = false;
            await this.fetchQuery({});
        },

        async updateAnalysisRequest(analysisRequestId, payload) {
            /* Delete spaces before and after the PO Name */
            const copyPayload = cloneDeep(payload);
            if (copyPayload.poNumber) copyPayload.poNumber = copyPayload.poNumber.trim();

            const requestPayload = this.preparePayload(copyPayload);

            const { data } = await this.$rq.updateAnalysisRequest(analysisRequestId, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('analysisRequests.updated', { name: data.requestNumber }),
            });
            await this.fetchQuery({});
            this.resetStore();
        },

        async removeAnalysisRequests() {
            await Promise.all(this.selectedAnalysisRequests.map(analysisRequest => this.$rq.removeAnalysisRequest(analysisRequest.id)));
            this.$toasted.global.toast_success({ message: this.$t('analysisRequests.removed') });
            this.selectedAnalysisRequests = [];
            await this.fetchQuery({});
        },
        checkIsItClickable({ item }) {
            return !item.labWorkOrder;
        },
        async redirectToWO(item) {
            await this.getWorkOrder(item.workOrderId);
            this.$router.push({
                name: 'WorkOrderView',
                params: {
                    clientId: item.client.id,
                    siteId: item.site.id,
                    workOrderId: item.workOrderId,
                },
            });
        },
        onClickRedirect(labId, clientid, siteid) {
            this.$router.push({
                name: 'labWorkOrderView',
                params: {
                    labWorkOrderId: labId,
                    clientId: clientid,
                    siteId: siteid,
                },
            });
        },
        async handleApplyFilters(filterParams) {
            this.queryParams = {
                ...this.queryParams,
                ...filterParams,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;


            // reset filter if needed
            if (filterParams?.reset) {
                this.getFilterValues(true);
            }


            await this.fetchQuery(this.queryParams);
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            this.fetchQuery(this.queryParams);
        },
        actionButton(item, action) {
            if (action.mode === 'edit') this.onClickUpdateAnalysisRequest(item, 'edit');
            else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('analysisRequests.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('analysisRequests.confirmRemove') },
                    onConfirm: async () => {
                        const responseRemove = await this.$rq.removeAnalysisRequest(item.id);
                        if (responseRemove.status === 200) await this.fetchQuery({});
                    },
                    isForm: false,
                });
            } else if (action.icon === 'mdi-file-export') {
                this.downloadFile(item);
            }
        },
        onCollectedByChange(item) {
            this.$root.$emit('showModal', {
                title: this.$t('analysisRequests.collectedBy'),
                component: Vue.component('CollectedByForm', CollectedByForm),
                showLangs: false,
                isForm: false,
                payload: { currentUser: item.requestedById, client: item.client.id },
                onConfirm: requestPayload => {
                    if (requestPayload) {
                        this.updateAnalysisRequest(item.id, { requestedBy: requestPayload });
                    }
                },
            });
        },
        resetStore() {
            this.$store.dispatch('setSample', { });
        },
        async loadDisplayedAnalysisRequests() {
            this.displayedRequests = this.analysisRequests.map(analysisRequest => ({
                ...analysisRequest,
                type: this.formatType(analysisRequest.type),
                typeOrigin: analysisRequest.type,
                request: analysisRequest.requestNumber,
                siteName: analysisRequest.site.name,
                deliveryAccountNumber: analysisRequest.site.deliveryAccountNumber,
                component: analysisRequest.component.name,
                date: this.$options.filters.formatDate(analysisRequest.createdAt, 'L LT'),
                requestedBy: analysisRequest.requestedBy.id
                    ? `${analysisRequest.requestedBy.user.firstName} ${analysisRequest.requestedBy.user.lastName}`
                    : this.$t('analysisRequests.noCollectedBy'),
                requestedById: analysisRequest.requestedBy.id,
                requestedByRole: analysisRequest.requestedBy.roles || [],
                workOrder: analysisRequest.workOrder ? analysisRequest.workOrder.contractNumber : '',
                workOrderId: analysisRequest.workOrder ? analysisRequest.workOrder.id : null,
                labWorkOrder: analysisRequest.labWorkOrder ? analysisRequest.labWorkOrder.woNumber : '',
                labWorkOrderId: analysisRequest.labWorkOrder ? analysisRequest.labWorkOrder.id : '',
                status: analysisRequest.status || analysisRequestStatus.RECEIVED,
                samples: this.prepareSamples(analysisRequest.samples),
            }));
        },
        getActions(analysisRequest) {
            let items = [];
            const editDel = [
                {
                    name: this.$t('forms.btn.edit'),
                    icon: 'mdi-pencil',
                    mode: 'edit',
                },
                {
                    name: this.$t('forms.btn.delete'),
                    icon: 'mdi-delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                },
            ];
            if (!this.hasRole(roles.ROLE_OPERATOR.value)) {
                items = editDel;
            }
            analysisRequest.requestedByRole.forEach(role => {
                if (role.key === roles.ROLE_OPERATOR.value && this.hasRole(roles.ROLE_OPERATOR.value)) {
                    items = editDel;
                }
            });
            if ([sampleTypes.LEGIONELLA_CULTRURE, sampleTypes.LEGIONELLOSIS].includes(analysisRequest.typeOrigin)) {
                const request = {
                    name: this.$t('forms.btn.request'),
                    icon: 'mdi-file-export',
                };
                items = ['INTREATMENT', 'COMPLETED'].includes(analysisRequest.status) ? [request] : [request, ...items];
            }
            return this.hasPermission('analysisRequests.edit') ? items : null;
        },
        async downloadFile(item) {
            if (item.traceability?.length) {
                const { data } = await this.$rq.downloadFile(item.traceability);
                this.$utils.saveFileOnDisk(data, `traceability_${item.requestNumber}.pdf`);
            }
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getAnalysisRequests();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },

    },
};
</script>

  <style lang="scss" scoped>
    .analysisRequest-action-bar {
        position: sticky;
        top: 64px;
        background: #fff;
        z-index: 8;
        box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
    }
    .linkWO {
      text-align: center;
    }
    .receivedDate {
      margin-bottom: 13px;
    }
  </style>


<style lang="scss">
@media screen and (max-width: 599px) {
    .blocSample {
        width: max-content ;
        tbody {
            td {
                display: flex;
                justify-content: flex-start;
                .v-data-table__mobile-row__cell {
                    margin-left: 15px;
                }
            }
        }
    }
}
</style>
